import React, { useEffect, useState } from "react";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { ArticleBoardType } from "@sellernote/shared/src/api/dto/entities/common/support";
import { ShipdaCurrentLanguage } from "@sellernote/shared/src/i18n/i18nForShipda";
import { getPageSize } from "@sellernote/shared/src/utils/etc";
import { getBoardHref } from "@sellernote/features/src/common/utils/support";
import { supportActions } from "@sellernote/shipda-kr/src/modules/support";
import { useAppSelector } from "@sellernote/shipda-kr/src/store";
import BoardList from "@sellernote/ui/src/sds-v1/components/board/BoardList";
import BoardPageTitle from "@sellernote/ui/src/sds-v1/components/board/BoardPageTitle";
import Loading from "@sellernote/ui/src/sds-v1/components/Loading";
import Paging from "@sellernote/ui/src/sds-v1/components/Paging";

import Styled from "../pages/support/index.styles";
import SupportBreadcrumb from "../pages/support/SupportBreadcrumb";
import SupportLayout from "../pages/support/SupportLayout";
import SupportSideMenu from "../pages/support/SupportSideMenu";

import Seo from "./Seo";

/**
 * `/support/onlinelecture`(`articleType`이 `video`인 케이스)는 해당 페이지에서 직접 넣어줌에 유의
 */
function getSeoContents({ articleType }: { articleType: ArticleBoardType }) {
  const seoContents: { title: string; description: string } = {
    title: "",
    description: "",
  };

  switch (articleType) {
    case "howto": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "이용방법 - 디지털 수출입 물류 포워더 쉽다"
          : "Guide - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "해상, 항공수출입 물류를 더 쉽게, 디지털 포워더 쉽다를 이용하는 방법을 안내합니다."
          : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services.";
      break;
    }
    case "terms": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "무역용어 및 운송프로세스 - 디지털 수출입 물류 포워딩 쉽다"
          : "Terms & Process - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "쉽다 포워딩 서비스를 이용할 때 꼭 필요한 무역용어와 수출 및 수입프로세스를 안내합니다."
          : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services.";
      break;
    }
    case "faq": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "FAQ - 디지털 수출입 물류 포워딩 쉽다"
          : "FAQ - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "디지털 포워딩 서비스 쉽다를 이용하시는 고객 분들이 자주 문의하는 질문을 안내 드립니다."
          : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services.";
      break;
    }
    case "notice": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "공지사항 - 디지털 수출입 물류 포워딩 쉽다"
          : "Notice - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "쉽다 포워딩 서비스에서 수출무역, 수입무역 고객사분들께 주요사항을 공지합니다."
          : "Notice for ShipDa customers to announce important information for our export/import customers about ocean FCL/LCL and AIR freight forwarding services.";
      break;
    }
  }

  return seoContents;
}

const PAGE_UNIT = 10;

export default function SupportBoardList({
  title,
  articleType,
}: {
  title: string;
  articleType: ArticleBoardType;
}) {
  const { t } = useTranslation();

  const router = useRouter();

  const dispatch = useDispatch();

  const { boardListData, boardContentsTotal } = useAppSelector((state) => {
    return {
      boardListData: state.support.GET_LIST.data?.list,
      boardContentsTotal: state.support.GET_LIST.data?.total,
    };
  });

  const [page, setPage] = useState(0);

  const seoContents = useMemo(
    () => getSeoContents({ articleType }),
    [articleType]
  );

  useEffect(() => {
    fetchList();

    return () => {
      dispatch(supportActions.INIT_GET_LIST({}));
    };
  }, [page]);

  function fetchList() {
    // articleType - 게시판 종류
    const params = { articleType, page, perPage: PAGE_UNIT };

    dispatch(supportActions.GET_LIST(params));
  }

  if (!boardListData)
    return (
      <Loading active={!boardListData} label={t("support-new:공통_로딩")} />
    );

  return (
    <SupportLayout>
      <Seo
        path={getBoardHref(articleType)}
        title={seoContents.title}
        description={seoContents.description}
      />
      <Styled.mainContainer>
        <SupportBreadcrumb />

        <BoardPageTitle titleStyle="default">{title}</BoardPageTitle>

        <Paging
          pageSize={getPageSize(PAGE_UNIT, boardContentsTotal)}
          currentPage={page}
          currentPageData={
            <BoardList
              listData={boardListData}
              boardItemHref={router.pathname}
              LinkComponent={Link}
            />
          }
          handleClickPage={(p) => setPage(p)}
          isZeroBasedPage
        />
      </Styled.mainContainer>

      <SupportSideMenu />
    </SupportLayout>
  );
}
